/**
 * Turning a stored address into something you can print on a label.
 *
 * An address arrives from reZEN as one flat line with no commas and the state
 * spelled out with an underscore:
 *
 *   "530 Rutledge Drive  Catawba SOUTH_CAROLINA 29704"
 *
 * A label needs:
 *
 *   530 Rutledge Drive
 *   Catawba, SC 29704
 *
 * The end of the line is reliable — the zip is last, the state is the token
 * before it — and both come off cleanly for all 730 addresses in the roster.
 * What is left is street and town with nothing marking the join. Most have a
 * run of two or more spaces there; the rest are matched against the towns
 * learned from the ones that don't need guessing.
 *
 * Where the town cannot be found, the whole remainder goes on one line. That
 * still posts perfectly well — the zip is what the sorting machine reads — and
 * `merged` is set so the caller can say how many came out that way.
 */

const STATES: Record<string, string> = {
  ALABAMA: 'AL', ALASKA: 'AK', ARIZONA: 'AZ', ARKANSAS: 'AR', CALIFORNIA: 'CA',
  COLORADO: 'CO', CONNECTICUT: 'CT', DELAWARE: 'DE', FLORIDA: 'FL', GEORGIA: 'GA',
  HAWAII: 'HI', IDAHO: 'ID', ILLINOIS: 'IL', INDIANA: 'IN', IOWA: 'IA',
  KANSAS: 'KS', KENTUCKY: 'KY', LOUISIANA: 'LA', MAINE: 'ME', MARYLAND: 'MD',
  MASSACHUSETTS: 'MA', MICHIGAN: 'MI', MINNESOTA: 'MN', MISSISSIPPI: 'MS',
  MISSOURI: 'MO', MONTANA: 'MT', NEBRASKA: 'NE', NEVADA: 'NV',
  NEW_HAMPSHIRE: 'NH', NEW_JERSEY: 'NJ', NEW_MEXICO: 'NM', NEW_YORK: 'NY',
  NORTH_CAROLINA: 'NC', NORTH_DAKOTA: 'ND', OHIO: 'OH', OKLAHOMA: 'OK',
  OREGON: 'OR', PENNSYLVANIA: 'PA', RHODE_ISLAND: 'RI', SOUTH_CAROLINA: 'SC',
  SOUTH_DAKOTA: 'SD', TENNESSEE: 'TN', TEXAS: 'TX', UTAH: 'UT', VERMONT: 'VT',
  VIRGINIA: 'VA', WASHINGTON: 'WA', WEST_VIRGINIA: 'WV', WISCONSIN: 'WI',
  WYOMING: 'WY', DISTRICT_OF_COLUMBIA: 'DC', PUERTO_RICO: 'PR',
}

export type ParsedAddress = {
  street: string
  city: string
  /** Two-letter abbreviation where the state was recognised, else as stored. */
  state: string
  zip: string
  /** True when street and town could not be told apart and share a line. */
  merged: boolean
  /**
   * True when the record holds more than one thing that looks like a zip —
   * usually a whole second address pasted in front of the old one, sometimes a
   * stray number left in the street. Six rows in the roster are like this. No
   * parser can pick between two addresses, and nor should it: they are worth a
   * human eye before they go in the post.
   */
  suspect: boolean
}

/**
 * More than one thing that looks like a zip code, ignoring a house number at
 * the very start and a PO Box number.
 *
 * A first attempt matched "a state followed by five digits", but uppercasing
 * the line first made that "any four-letter word followed by five digits" —
 * which is not what it claimed, even though it happened to catch the right
 * rows. This rule is what it says.
 *
 * Every address ends in one zip. A second one means something else got pasted
 * in: a whole previous address, or a stray number sitting in the street.
 *
 *   "12323 McAllister Park Drive  Charlotte NORTH_CAROLINA 28277"  fine
 *   "2538 Tillman Street 5960 Fairview Rd, Charlotte NC 28210 Monroe … 28112"  not
 */
const UNIT_WORDS = new Set(['apt', 'unit', 'ste', 'suite', 'bldg', 'lot', 'no', 'box'])

export function looksDoubled(raw: string | null | undefined): boolean {
  if (!raw) return false
  const words = raw.trim().split(/\s+/)
  let zips = 0
  for (let i = 0; i < words.length; i++) {
    if (!/^\d{5}(?:-\d{4})?[,.]?$/.test(words[i])) continue
    if (i === 0) continue // a house number
    const before = (words[i - 1] ?? '').toLowerCase().replace(/[.#,]/g, '')
    // Box, apartment, unit and lot numbers are legitimately five digits.
    if (UNIT_WORDS.has(before)) continue
    zips++
  }
  return zips > 1
}

const squash = (v: string) => v.replace(/\s+/g, ' ').trim()

/**
 * Words that stay lowercase inside a name. The surname particles, and "and" —
 * which appears when two people at one address share a label, and which read
 * as "Ben Jaeger And Sarah Jaeger" until it was added here.
 */
const PARTICLES = new Set([
  'de', 'del', 'della', 'der', 'di', 'du', 'la', 'le', 'van', 'von', 'da', 'dos', 'and',
])

const capitalise = (w: string) =>
  w.replace(/^[a-z]/, (m) => m.toUpperCase()).replace(/^Mc([a-z])/, (_m, c: string) => 'Mc' + c.toUpperCase())

/**
 * Case for envelopes. ALL CAPS and all-lowercase both appear in this data and
 * both look wrong on the outside of a letter. A name already typed properly is
 * left alone apart from a word that is entirely lowercase.
 *
 * It judges the whole string, so it must be applied to ONE name at a time.
 * "BEN JAEGER and SARAH JAEGER" contains lower-case letters — from the "and" —
 * and so reads as already-mixed and is left shouting.
 */
export function tidyCase(v: string): string {
  const hasLower = /[a-z]/.test(v)
  const hasUpper = /[A-Z]/.test(v)
  if (hasLower && hasUpper) {
    // Mostly typed properly already. Only lift a word that is entirely
    // lowercase — "Anthony hughes" — and leave particles and anything with
    // capitals of its own ("DeKanchuk", "de Silva") exactly as written.
    return v
      .split(' ')
      .map((w) => (/^[a-z'’-]+$/.test(w) && !PARTICLES.has(w.toLowerCase()) ? capitalise(w) : w))
      .join(' ')
  }
  return v
    .toLowerCase()
    .split(' ')
    .map((w) => (PARTICLES.has(w) ? w : capitalise(w)))
    .join(' ')
    .replace(/\bPo Box\b/g, 'PO Box')
}

const STATE_NAMES = Object.keys(STATES).sort((a, b) => b.length - a.length)

/**
 * Deliberately run against the ORIGINAL string rather than a squashed one, so
 * that `rest` keeps the double spaces that mark where the town begins.
 */
const TAIL = new RegExp(
  `^([\\s\\S]*?)\\s+(${STATE_NAMES.join('|')})\\s+(\\d{5}(?:-\\d{4})?)\\s*$`,
  'i',
)

/**
 * The words a street name ends with. Used only as a last resort, when neither
 * the source's own spacing nor a known town name has worked.
 */
const STREET_TYPES = new Set(
  ('st street rd road dr drive ln lane ct court ave avenue blvd boulevard way' +
    ' cir circle pl place ter terrace trl trail pkwy parkway hwy highway pt point' +
    ' run loop path xing crossing sq square row bnd bend cv cove ridge creek' +
    ' walk grove landing crest knoll view').split(' '),
)

const DIRECTIONS = new Set(['n', 's', 'e', 'w', 'ne', 'nw', 'se', 'sw', 'north', 'south', 'east', 'west'])

/**
 * Does this read like the name of a town?
 *
 * The double-space rule takes the last chunk as the town, but the source's
 * spacing is not always where you'd hope: "1007 Johnnie Dodds Blvd  Suite 115
 * Mount Pleasant" puts the break after the street, and without this the suite
 * number ends up printed on the city line — which is where the post office
 * looks for the town.
 */
const looksLikeTown = (v: string): boolean => {
  const words = v.split(' ')
  if (!v || /\d/.test(v)) return false
  if (words.length > 4) return false
  return !UNIT_WORDS.has(words[0].toLowerCase().replace(/\./g, ''))
}

/** Split what is left of an address into its runs of two-or-more spaces. */
const chunksOf = (rest: string) => rest.split(/\s{2,}/).map(squash).filter(Boolean)

/**
 * Learn the town names from the addresses that mark them with a double space,
 * so the ones that don't can be split too.
 *
 * Built from the whole roster rather than from whatever is being exported —
 * otherwise exporting a single agent would have nothing to learn from.
 */
export function learnCities(addresses: (string | null | undefined)[]): Set<string> {
  const cities = new Set<string>()
  for (const raw of addresses) {
    if (!raw) continue
    const m = TAIL.exec(raw.replace(/\s+$/, ''))
    if (!m) continue
    const chunks = chunksOf(m[1])
    if (chunks.length < 2) continue
    const town = chunks[chunks.length - 1]
    // A town is words, not a house number, and not a suite.
    if (town.length > 2 && looksLikeTown(town)) cities.add(town.toLowerCase())
  }
  return cities
}

const mark = (raw: string, p: Omit<ParsedAddress, 'suspect'>): ParsedAddress => ({
  ...p,
  suspect: looksDoubled(raw),
})

export function parseAddress(
  raw: string | null | undefined,
  cities?: Set<string>,
): ParsedAddress | null {
  if (!raw || !raw.trim()) return null

  const original = raw.replace(/\s+$/, '')
  const m = TAIL.exec(original)
  if (!m) {
    // No state and zip on the end. Nothing reliable to take apart, so keep the
    // line whole rather than inventing a split.
    return mark(original, { street: tidyCase(squash(original)), city: '', state: '', zip: '', merged: true })
  }

  const state = STATES[m[2].toUpperCase().replace(/\s+/g, '_')] ?? m[2]
  const zip = m[3]
  const rest = m[1]

  // Preferred: the source put a run of spaces between street and town. Taking
  // the state and zip off FIRST matters — "1518 Bowater Road  Rock Hill
  // SOUTH_CAROLINA 29732" has two such runs, and the last one is the state.
  const chunks = chunksOf(rest)
  if (chunks.length >= 2) {
    const town = chunks[chunks.length - 1]
    const street = chunks.slice(0, -1).join(' ')
    if (town && street && looksLikeTown(town)) {
      return mark(original, { street: tidyCase(street), city: tidyCase(town), state, zip, merged: false })
    }
  }

  // Otherwise: match the end of the line against towns seen elsewhere, longest
  // first, so "North Myrtle Beach" wins over "Myrtle Beach".
  if (cities && cities.size) {
    const words = squash(rest).split(' ')
    for (let take = Math.min(4, words.length - 1); take >= 1; take--) {
      const candidate = words.slice(words.length - take).join(' ')
      if (cities.has(candidate.toLowerCase())) {
        const street = words.slice(0, words.length - take).join(' ')
        if (street) {
          return mark(original, { street: tidyCase(street), city: tidyCase(candidate), state, zip, merged: false })
        }
      }
    }
  }

  // Last resort before giving up: a street almost always ends in a street-type
  // word. "127 Red Birch Ln Warner Robins" splits after "Ln" — a town we have
  // never seen, found without a dictionary.
  const words = squash(rest).split(' ')
  // Forward, not backward. Backwards takes the LAST street-type word, and
  // "Oak Street Ridge Spring" would cut after "Ridge" — splitting a real town
  // (Ridge Spring, Point Pleasant, Cove City) down the middle and looking
  // entirely plausible while doing it.
  for (let i = 1; i <= words.length - 2; i++) {
    if (!STREET_TYPES.has(words[i].toLowerCase().replace(/\./g, ''))) continue
    let cut = i + 1
    // "Heritage Crossing Pt SW" — a compass direction after the street type is
    // part of the street, not the first word of the town.
    if (DIRECTIONS.has(words[cut]?.toLowerCase().replace(/\./g, ''))) cut += 1
    // A unit that follows the street type belongs to the street too.
    if (UNIT_WORDS.has(words[cut]?.toLowerCase().replace(/\./g, ''))) cut += 2
    else if (/^#/.test(words[cut] ?? '')) cut += 1
    // "(Optional)" and the like are form artefacts sitting in the data. They
    // are not part of a town's name.
    while (/^\(.*\)$/.test(words[cut] ?? '')) cut += 1
    const tail = words.slice(cut)
    if (tail.length >= 1 && tail.length <= 3 && !tail.some((w) => /\d/.test(w))) {
      return mark(original, {
        street: tidyCase(words.slice(0, cut).join(' ')),
        city: tidyCase(tail.join(' ')),
        state,
        zip,
        merged: false,
      })
    }
  }

  // Give up honestly rather than guess.
  return mark(original, { street: tidyCase(squash(rest)), city: '', state, zip, merged: true })
}

/**
 * The lines of a mailing label, blanks removed.
 *
 * The name gets the same case treatment as the address: "ADRIAN NEDELEA" is
 * how the record was typed, not how anyone wants to be addressed on an
 * envelope. A name already mixed — "Christopher DeKanchuk" — is left exactly
 * as it is, which is the whole point of the rule.
 */
export function labelLines(name: string, a: ParsedAddress): string[] {
  const lastLine = [a.city, [a.state, a.zip].filter(Boolean).join(' ')]
    .filter(Boolean)
    .join(', ')
  return [tidyCase(squash(name ?? '')), a.street, lastLine]
    .map((l) => squash(l ?? ''))
    .filter(Boolean)
}
